// Full Row Detail with several categorization rule sets: what each selected rule set adds
// to the detail query. Shared by the SQL preview (sqlPreview.ts), the Excel layout aliases
// (workbookLayout.ts), the metric editor and validation.
//
// Semantics - mirrored by ReportSqlBuilder.BuildDetailRuleSetParts on the server:
//   - Every selected rule set becomes exactly ONE output column, whatever its rules target.
//     Its "Set" rules, in rule order, are the WHEN branches of that column's CASE; the
//     column is shown under the alias the user gave it (default: the rule set's name).
//   - Each rule set is evaluated independently against the source row; the original data
//     columns are left as they are (a rule set that targets a real column produces the
//     recoded value in its own column instead of overwriting the original).
//   - ELSE: the rule set's else label; nothing (NULL) when "hide items without a matching
//     rule" is on; otherwise the targeted column's own value when every Set rule targets the
//     same real column, else the metric's breakdown value (the rule sets' usual fallback).
//   - Include/exclude rules and "hide items without a matching rule" filter rows. Filters from
//     every selected rule set apply together (AND).
import type { ReportLogicContext } from "./context";
import type { DataColumn, MetricSection, ReportConfig, RuleSet } from "../types";
import { buildRuleFilterClauses, ruleLogicComment, ruleResultSql, ruleSetModelKey, setRuleMatchExpression } from "./ruleInterpretation";
import { targetFieldDef } from "./catalogResolution";
import { sqlLiteral } from "./sqlExpressions";
import { relatedResultTextSql } from "./relatedTables";
import { ruleHasNestedGroup } from "./rules";
import { detailRuleSetAlias, detailRuleSetDisplayName, selectedDetailRuleSetIds, usesDetailRuleSetList } from "./detailRuleSetSelection";

export interface DetailRuleSetOutput {
    ruleSetId: string;
    ruleSet?: RuleSet;
    // Technical key: the server's SELECT alias and the Excel layout sourceAlias.
    alias: string;
    // The column name shown in the query preview and used as the default sheet header:
    // the user's alias for this selection, else the rule set's name.
    label: string;
    // The rule set's CASE expression, or null when it has no complete Set rules.
    columnExpr: string | null;
    filterClauses: string[];
}

// Also used by combined categorizations (logic/sqlPreview.ts buildCombinedRuleSetsSql): the
// same one-CASE-per-rule-set semantics, mirrored server-side by BuildRuleSetCaseExpr.
export function ruleSetCaseExpr(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet, useIds: boolean, categoryFallbackColumn: DataColumn | null) {
    const whens: string[] = [];
    const targets = new Set<string>();
    const modelKey = ruleSetModelKey(context, report, ruleSet);
    ruleSet.rules.forEach((rule, ruleIndex) => {
        if (rule.action !== "set") return;
        const result = ruleResultSql(context, report, rule, modelKey);
        if (!result) return;
        const expression = setRuleMatchExpression(context, report, rule, useIds, modelKey);
        if (!expression) return;
        targets.add(rule.targetField || "category");
        if (ruleHasNestedGroup(rule.conditions)) whens.push(`-- Rule ${ruleIndex + 1} logic: ${ruleLogicComment(context, report, rule)}`);
        whens.push(`WHEN ${expression} THEN ${result}`);
    });
    if (!whens.length) return null;

    const customElse = ruleSet.elseLabel.trim();
    const onlyTarget = targets.size === 1 ? Array.from(targets)[0] : "";
    const onlyTargetField = onlyTarget && onlyTarget !== "category" ? targetFieldDef(context, onlyTarget, report) : null;
    const elseValue = ruleSet.includeElse === false
        ? ""
        : customElse
            ? sqlLiteral(customElse)
            // Column fallbacks as text: the THEN branches are NVARCHAR (see resultTextSqlForColumn).
            : onlyTargetField && !onlyTargetField.virtual
                ? relatedResultTextSql(onlyTargetField)
                : categoryFallbackColumn ? relatedResultTextSql(categoryFallbackColumn) : sqlLiteral("Everything Else");
    return `CASE\n${whens.map((line) => `      ${line}`).join("\n")}${elseValue ? `\n      ELSE ${elseValue}` : ""}\n    END`;
}

export function detailRuleSetOutputs(
    context: ReportLogicContext,
    report: ReportConfig | null | undefined,
    section: MetricSection,
    useIds = false,
    categoryFallbackColumn: DataColumn | null = null,
): DetailRuleSetOutput[] {
    if (!report || !usesDetailRuleSetList(section)) return [];
    return selectedDetailRuleSetIds(section).map((ruleSetId) => {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === ruleSetId);
        return {
            ruleSetId,
            ruleSet,
            alias: detailRuleSetAlias(ruleSetId),
            label: detailRuleSetDisplayName(section, ruleSetId, ruleSet?.name),
            columnExpr: ruleSet ? ruleSetCaseExpr(context, report, ruleSet, useIds, categoryFallbackColumn) : null,
            filterClauses: ruleSet ? buildRuleFilterClauses(context, report, ruleSet, useIds) : [],
        };
    });
}
